import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import FormElementHook from 'components/form/form-element-hook';
import { useForm } from 'react-hook-form';
import { FormAdjustmentDefinition, RegistrationFormAdjustment } from '../AddOnFees/models';
import { Button, Icon } from '@teamsnap/teamsnap-ui';
import * as CreateFormAdjustmentDefinition from 'core/gql/mutations/create-form-adjustment-defnition';
import * as UpdateFormAdjustmentDefinition from 'core/gql/mutations/update-form-adjustment-definition';
import * as CreateFormAdjustment from 'core/gql/mutations/create-form-adjustment';
import * as UpdateFormAdjustment from 'core/gql/mutations/update-form-adjustment';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as FormAdjustmentDefinitions from 'core/gql/queries/form-adjustment-definitions';
import * as ShowOrganization from 'core/gql/queries/show-organizations';

import './FeeModal.scss';

const CreateElement = FormElementHook as new () => FormElementHook;

type Props = {
  orgId: number;
  formId: number;
  adjustmentDefinition: FormAdjustmentDefinition;
  adjustment: RegistrationFormAdjustment;
  show: boolean;
  formFieldId: number;
  currentOption: string;
  onClose: () => void;
};

export const UNLIMITED_FEE_MAX_AMOUNT = 999999;

const showError = () => {
  reactiveFeedbackMessage({
    message: 'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
    isError: true,
    autoclose: true,
  });
};

const showSuccess = (message) => {
  reactiveFeedbackMessage({
    message: message,
    isError: false,
    autoclose: true,
  });
};

const FeeModal = ({
  orgId,
  formId,
  adjustmentDefinition,
  adjustment,
  show,
  formFieldId,
  currentOption,
  onClose,
}: Props) => {
  const [hasRange, setHasRange] = React.useState<boolean>(false);
  const [amount, setAmount] = React.useState<String>(null);
  const [maxAmount, setMaxAmount] = React.useState<String>(null);
  const [isAmountNegative, setIsAmountNegative] = React.useState<boolean>(false);

  const refetch = {
    refetchQueries: [
      { query: FormAdjustmentDefinitions.QUERY, variables: { organizationId: +orgId } },
      { query: RegistrationFormAdjustments.QUERY, variables: { formId: +formId } },
    ],
  };

  const [createFormAdjustmentDefinition] = useMutation(CreateFormAdjustmentDefinition.MUTATION, refetch);
  const [updateFormAdjustmentDefinition] = useMutation(UpdateFormAdjustmentDefinition.MUTATION, refetch);
  const [createFormAdjustment] = useMutation(CreateFormAdjustment.MUTATION, refetch);
  const [updateFormAdjustment] = useMutation(UpdateFormAdjustment.MUTATION, refetch);

  const showOrgQuery = ShowOrganization.QUERY;

  const getOrganization = useQuery(showOrgQuery, {
    variables: { organizationId: +orgId },
    skip: !orgId,
  });

  const organization = getOrganization.data?.showOrganization;

  const adjustmentForm = useForm();

  const { handleSubmit, register, setValue, watch } = adjustmentForm;

  React.useEffect(() => {
    setValue('name', adjustmentDefinition?.name ?? '');
    setValue('category', adjustmentDefinition?.category ?? 'Merchandise');

    if (adjustment?.id && adjustmentDefinition?.id === adjustment?.formAdjustmentDefinition.id) {
      setIsAmountNegative(Math.sign(adjustment?.amount) === -1);
      setAmount(adjustment?.amount ? (Math.abs(adjustment?.amount) / 100).toFixed(2) : null);
      setMaxAmount(adjustment?.maxAmount ? (adjustment?.maxAmount / 100).toFixed(2) : '');
      if (adjustment?.maxAmount > 0) {
        setHasRange(true);
      }
    } else {
      setIsAmountNegative(Math.sign(adjustmentDefinition?.amount) === -1);
      setAmount(adjustmentDefinition?.amount ? (Math.abs(adjustmentDefinition?.amount) / 100).toFixed(2) : null);
      setMaxAmount(
        adjustmentDefinition?.maxAmount
          ? adjustmentDefinition?.maxAmount / 100 === UNLIMITED_FEE_MAX_AMOUNT
            ? (adjustmentDefinition?.maxAmount / 100).toFixed(2)
            : ''
          : ''
      );
      if (adjustmentDefinition?.maxAmount > 0) {
        setHasRange(true);
      }
    }
  }, [adjustment, adjustmentDefinition, setValue]);

  React.useEffect(() => {
    setValue('max_amount', maxAmount ?? '');
    setValue('amount', amount);
  }, [amount, maxAmount, hasRange, setValue]);

  const saveAdjustmentDefinition = async (values) => {
    try {
      const amount = Math.round(parseFloat(values.amount) * 100 * (isAmountNegative ? -1 : 1)) || 0;
      const maxAmount =
        Math.round(parseFloat(values.max_amount !== '' ? values.max_amount : UNLIMITED_FEE_MAX_AMOUNT) * 100) || 0;

      if (adjustmentDefinition) {
        if (values.saveToLibrary) {
          const variables = {
            id: adjustmentDefinition.id,
            organizationId: +orgId,
            amount,
            maxAmount,
          };
          const response = await updateFormAdjustmentDefinition({ variables });
          if (response?.data?.updateFormAdjustmentDefinition?.id) {
            showSuccess('Library Fee Updated');
            const variables = {
              formAdjustmentDefinitionId: response?.data?.updateFormAdjustmentDefinition?.id,
            };
            await createOrUpdateFormAdjustment({ ...values, ...variables });
          } else {
            console.error(response);
            showError();
          }
        } else {
          createOrUpdateFormAdjustment({ ...values, formAdjustmentDefinitionId: adjustmentDefinition.id });
        }
      } else {
        const variables = {
          organizationId: +orgId,
          name: values.name,
          category: values.category,
          amount,
          maxAmount,
        };
        const response = await createFormAdjustmentDefinition({ variables });

        if (response?.data?.createFormAdjustmentDefinition?.id) {
          showSuccess('Library Fee Created');
          const variables = {
            formAdjustmentDefinitionId: response?.data?.createFormAdjustmentDefinition?.id,
          };
          await createOrUpdateFormAdjustment({ ...values, ...variables });
        } else {
          console.error(response);
          showError();
        }
      }
    } catch (e) {
      console.error(e);
      showError();
    }
  };

  const createOrUpdateFormAdjustment = async (values) => {
    const amount = Math.round(parseFloat(values.amount) * 100 * (isAmountNegative ? -1 : 1)) || 0;
    const maxAmount =
      Math.round(parseFloat(values.max_amount !== '' ? values.max_amount : UNLIMITED_FEE_MAX_AMOUNT) * 100) || 0;

    if (adjustment?.id) {
      const variables = {
        id: +adjustment.id,
        formId: +formId,
        formFieldId: formFieldId,
        formAdjustmentDefinitionId: +adjustmentDefinition.id,
        amount,
        maxAmount,
      };
      const response = await updateFormAdjustment({ variables });
      if (response?.data?.updateRegistrationFormAdjustment?.id) {
        showSuccess('Fee Updated');
        closeModal();
      } else {
        console.error(response);
        showError();
      }
    } else {
      const variables = {
        formId: +formId,
        formFieldId: formFieldId,
        formAdjustmentDefinitionId: +values.formAdjustmentDefinitionId ?? +adjustmentDefinition.id,
        answerToMatch: currentOption,
        enabled: true,
        max_quantity: 1,
        amount,
        maxAmount,
      };
      const response = await createFormAdjustment({ variables });
      if (response?.data?.createRegistrationFormAdjustment?.id) {
        showSuccess('Fee Created');
        closeModal();
      } else {
        console.error(response);
        showError();
      }
    }
  };

  const closeModal = () => {
    onClose();
    setHasRange(false);
  };

  return (
    <>
      {show && (
        <div id="fee-modal">
          <div className="fee-modal__content">
            <h3>{adjustmentDefinition ? 'Edit Fee' : 'Create Fee'}</h3>
            <div id="fee-modal-form">
              <div className="fee-form__field">
                <CreateElement
                  inputRef={register({
                    required: 'You must select a category for the fee.',
                  })}
                  useHookResponse={adjustmentForm}
                  type="select"
                  field="category"
                  label={
                    <>
                      Fee Category<span className="u-colorNegative">*</span>
                    </>
                  }
                  autoComplete="off"
                  disabled={adjustmentDefinition ? true : false}
                  options={[
                    {
                      text: 'Merchandise',
                      value: 'Merchandise',
                    },
                    {
                      text: 'Donation',
                      value: 'Donation',
                    },
                    {
                      text: 'Membership Fee',
                      value: 'Membership Fee',
                    },
                    {
                      text: 'Registration Fee',
                      value: 'Registration Fee',
                    },
                    {
                      text: 'Other',
                      value: 'Other',
                    },
                  ]}
                />
                <small>This will be used for org-level financial reporting.</small>
              </div>

              <div className="fee-form__field">
                <CreateElement
                  inputRef={register({
                    required: 'You must enter a name for the fee.',
                  })}
                  useHookResponse={adjustmentForm}
                  type="text"
                  field="name"
                  label={
                    <>
                      Fee Name<span className="u-colorNegative">*</span>
                    </>
                  }
                  placeholder="Name"
                  autoComplete="off"
                  disabled={adjustmentDefinition ? true : false}
                />
                <small>This will show up in registrant's cart.</small>
              </div>

              <div className="fee-form__field">
                <h4>
                  Amount<span className="u-colorNegative">*</span>
                </h4>
                <div className="u-padTopSm">
                  <Button
                    type="button"
                    mods={`u-borderNone u-borderRadiusNone ${hasRange ? 'u-colorNeutral7' : 'u-colorSuccess6 u-bgNeutral2'
                      }`}
                    onClick={() => setHasRange(false)}
                    testId="fee-modal-set-amount-button"
                  >
                    {!hasRange && <Icon name="check" mods="u-fontSizeXs u-spaceRightXs" />}
                    Set Amount
                  </Button>
                  <Button
                    type="button"
                    mods={`u-borderNone u-borderRadiudNone ${hasRange ? 'u-colorSuccess6 u-bgNeutral2' : 'u-colorNeutral7'
                      }`}
                    onClick={() => setHasRange(true)}
                    testId="fee-modal-range-button"
                  >
                    Range
                  </Button>
                </div>

                {!hasRange && (
                  <div className="u-bgNeutral2 u-padMd">
                    <CreateElement
                      inputRef={register({
                        required: 'You must enter an amount for the fee.',
                      })}
                      useHookResponse={adjustmentForm}
                      type="number"
                      field="amount"
                      placeholder="0.00"
                      autoComplete="off"
                      leftIcon={<span style={{ color: 'black' }}>{organization?.currencySymbol || ''}</span>}
                      rightIcon={<span style={{ color: 'black' }}>{organization?.currency || ''}</span>}
                      isNegativeNumber={isAmountNegative}
                      onSignChange={(isNegative) => {
                        setIsAmountNegative(isNegative);
                      }}
                    />
                    <small>To change the number from positive/negative click the "+" or "-".</small>
                  </div>
                )}

                {hasRange && (
                  <div className="u-bgNeutral2 u-padMd u-flex">
                    <div className="u-size1of2">
                      <CreateElement
                        inputRef={register({
                          required: 'You must enter an amount for setting a range.',
                        })}
                        useHookResponse={adjustmentForm}
                        type="number"
                        field="amount"
                        placeholder="0.00"
                        autoComplete="off"
                        leftIcon={<span style={{ color: 'black' }}>{organization?.currencySymbol || ''}</span>}
                        rightIcon={<span style={{ color: 'black' }}>{organization?.currency || ''}</span>}
                      />
                    </div>
                    <div className="u-spaceSm">To</div>
                    <div className="u-size1of2">
                      <CreateElement
                        inputRef={register({
                          validate: {
                            greaterThanBaseAmount: (v) =>
                              !v ||
                              parseFloat(v) > parseFloat(watch('amount')) ||
                              'You must enter an amount greater than base amount',
                          },
                        })}
                        useHookResponse={adjustmentForm}
                        type="number"
                        field="max_amount"
                        placeholder={'Unlimited'}
                        autoComplete="off"
                        leftIcon={<span style={{ color: 'black' }}>{organization?.currencySymbol || ''}</span>}
                        rightIcon={<span style={{ color: 'black' }}>{organization?.currency || ''}</span>}
                      />
                    </div>
                  </div>
                )}
              </div>

              {adjustmentDefinition && (
                <div className="fee-form__field">
                  <CreateElement
                    inputRef={register({})}
                    useHookResponse={adjustmentDefinition}
                    type="checkbox"
                    field="saveToLibrary"
                    label="Save changes to fee library."
                  />
                  <small>
                    If selected, edits to this fee will be saved to library. If un-selected, edits to this fee only
                    apply to this registration.
                  </small>
                </div>
              )}
            </div>
            <div className="fee-modal__actions">
              <Button testId="fee-modal-cancel-button" type="button" mods="u-spaceRightSm" onClick={() => closeModal()}>
                Cancel
              </Button>
              <Button
                mods="Button Button--primary"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(saveAdjustmentDefinition)();
                }}
                testId="fee-modal-save-button"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeeModal;
